import { useEffect, useRef } from 'react'
import { Box, Button, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'

const MATRIX_WORDS = [
  'SAT',
  '1600',
  '1590',
  '1580',
  '1570',
  '1600',
  '1560',
  '1550',
  '1600',
  '800',
  '790',
  '1600',
  '780',
  'MATH',
  '1600',
  'READ',
  'WRITE',
  '1600',
  'PREP',
  'A+',
]

export function Land() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const fontSize = 18
    const lineHeight = Math.floor(fontSize * 1.35)
    // Column width sized to fit the widest expected word ("WRITE") comfortably.
    const colWidth = fontSize * 4

    let columns: number[] = []
    let animationId = 0
    let lastFrame = 0

    const resize = () => {
      const { innerWidth, innerHeight } = window
      // Account for device pixel ratio so text stays crisp on hi-DPI screens.
      const dpr = window.devicePixelRatio || 1
      canvas.width = innerWidth * dpr
      canvas.height = innerHeight * dpr
      canvas.style.width = `${innerWidth}px`
      canvas.style.height = `${innerHeight}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const numCols = Math.ceil(innerWidth / colWidth)
      columns = new Array(numCols).fill(0).map(() => Math.random() * -innerHeight)

      // Fully black background on resize so the fade trail starts clean.
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, innerWidth, innerHeight)
    }

    resize()
    window.addEventListener('resize', resize)

    const draw = (time: number) => {
      animationId = requestAnimationFrame(draw)
      // Throttle controls fall speed: larger interval = slower rain.
      if (time - lastFrame < 180) return
      lastFrame = time

      const width = canvas.clientWidth
      const height = canvas.clientHeight

      // Semi-transparent black overlay creates the fading trail behind the head.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)'
      ctx.fillRect(0, 0, width, height)

      ctx.font = `bold ${fontSize}px "Courier New", "Lucida Console", monospace`
      ctx.textBaseline = 'top'

      for (let i = 0; i < columns.length; i++) {
        const word = MATRIX_WORDS[Math.floor(Math.random() * MATRIX_WORDS.length)]
        const x = i * colWidth
        const y = columns[i]

        // Clear this row's slot first so a shorter new word can't reveal
        // leftover pixels of a longer previous word in the same column.
        ctx.fillStyle = '#000'
        ctx.fillRect(x, y, colWidth, lineHeight)

        // Bright "head" of the stream. The trail is produced by the
        // semi-transparent black overlay above fading prior frames.
        ctx.fillStyle = 'rgba(180, 255, 200, 0.95)'
        ctx.fillText(word, x, y)

        columns[i] += lineHeight

        // Randomly reset streams once they pass the bottom for irregular rain.
        if (columns[i] > height && Math.random() > 0.975) {
          columns[i] = Math.random() * -200
        }
      }
    }

    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <Box
      style={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: '#000',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />

      <Box
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Subtle radial vignette pulls focus to the hero text.
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.95) 100%)',
          pointerEvents: 'none',
        }}
      >
        <Stack gap="lg" align="center" style={{ pointerEvents: 'auto', padding: '0 24px' }}>
          <Text
            fz={{ base: 32, sm: 56, md: 72 }}
            fw={800}
            ta="center"
            style={{
              color: '#FFFFFF',
              fontFamily: '"Courier New", "Lucida Console", monospace',
              textShadow: '0 0 14px rgba(255, 255, 255, 0.55), 0 0 32px rgba(255, 255, 255, 0.25)',
              letterSpacing: 2,
            }}
          >
            You have a dream score.
          </Text>

          <Text
            fz={{ base: 32, sm: 56, md: 72 }}
            fw={800}
            ta="center"
            style={{
              color: '#FFFFFF',
              fontFamily: '"Courier New", "Lucida Console", monospace',
              textShadow: '0 0 14px rgba(255, 255, 255, 0.55), 0 0 32px rgba(255, 255, 255, 0.25)',
              letterSpacing: 2,
            }}
          >
            We have a solution.
          </Text>

          <Text
            fz={{ base: 18, sm: 24, md: 28 }}
            fw={500}
            ta="center"
            mt="md"
            style={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontFamily: '"Courier New", "Lucida Console", monospace',
              letterSpacing: 4,
            }}
          >
            It just works.
          </Text>

          <Button
            component={Link}
            to="/"
            size="xl"
            radius="xl"
            mt="xl"
            style={{
              background: 'transparent',
              border: '2px solid #00FF66',
              color: '#00FF66',
              fontFamily: '"Courier New", "Lucida Console", monospace',
              fontWeight: 700,
              letterSpacing: 4,
              paddingLeft: 48,
              paddingRight: 48,
              boxShadow: '0 0 18px rgba(0, 255, 102, 0.45)',
            }}
          >
            START
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}
